'use client';

import { Children, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Section } from '../../../features/content-sections/ui/components/Section';
import { useDragScroll } from './hooks/useDragScroll';
import CarouselNavigation from './CarouselNavigation';
import { Css, ITheme, Responsive, themed } from '../../../ui';
import { StyledCarouselItem } from './CarouselItem';

export interface ICarouselProps {
	children?: React.ReactNode;
	desktopCardWidth?: number;
	mobileCardWidth?: number;
	gap?: number;
}

export default function Carousel({
	children,
	desktopCardWidth = 486,
	mobileCardWidth = 253,
	gap = 24,
}: ICarouselProps) {
	const [containerRef, { handleMouseDown, handleMouseMove, handleMouseUp }] =
		useDragScroll();
	const [currentIndex, setCurrentIndex] = useState(0);

	const updateCurrentIndex = () => {
		if (!containerRef.current) return;

		const container = containerRef.current;

		const firstCard = container.querySelector('[data-carousel-card]');

		if (!firstCard) return;

		// Get the width of a single carousel card to calculate scrolling positions
		const cardWidth = firstCard.getBoundingClientRect().width;

		const beforeWidth =
			container.firstElementChild?.getBoundingClientRect().width || 0;

		const adjustedScrollLeft = container.scrollLeft + beforeWidth;
		const index = Math.round(adjustedScrollLeft / (cardWidth + gap));

		setCurrentIndex(index - 1);
	};

	useEffect(() => {
		const container = containerRef.current;
		container?.addEventListener('scroll', updateCurrentIndex);

		return () => {
			container?.removeEventListener('scroll', updateCurrentIndex);
		};
	}, []);

	const scrollToIndex = (index: number) => {
		if (containerRef.current) {
			const cardWidth =
				containerRef.current.children[0].getBoundingClientRect().width;
			const totalScrollableWidth =
				containerRef.current.scrollWidth - containerRef.current.offsetWidth;

			const targetScrollLeft = Math.min(
				index * (cardWidth + gap),
				totalScrollableWidth,
			);

			containerRef.current.scrollTo({
				left: targetScrollLeft,
				behavior: 'smooth',
			});
		}
	};

	const handlePreviousClick = () =>
		scrollToIndex(Math.max(currentIndex - 1, 0));
	const handleNextClick = () => scrollToIndex(Math.max(currentIndex + 1, 0));
	const handleDotClick = (index: number) => scrollToIndex(index);
	const childrenCount = Children.count(children);

	return (
		<>
			<CarouselWrapper
				ref={containerRef}
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseUp}
				onMouseUp={handleMouseUp}
				gap={gap}
				desktopCardWidth={desktopCardWidth}
				mobileCardWidth={mobileCardWidth}
				role="region"
				aria-label="Image carousel"
				aria-roledescription="carousel"
			>
				{children}
			</CarouselWrapper>
			{childrenCount > 1 && (
				<NavigationSection>
					<CarouselNavigation
						currentIndex={currentIndex}
						totalItems={Children.count(children)}
						onPrev={handlePreviousClick}
						onNext={handleNextClick}
						onDotClick={handleDotClick}
						getKey={(index) => `dot-${index}`}
						previousLabel="Previous"
						nextLabel="Next"
					/>
				</NavigationSection>
			)}
		</>
	);
}

interface ICarouselWrapperProps {
	gap: number;
	desktopCardWidth: number;
	mobileCardWidth: number;
}

const CarouselWrapper = styled.ul<ICarouselWrapperProps, ITheme>`
	--gap: ${({ gap }) => Css.pxToRem(gap)};
	--content-width: ${themed(({ layout }) => layout.main.maxWidth)};
	--card-width: ${({ desktopCardWidth }) => desktopCardWidth}px;

	&:first-of-type {
		margin-left: ${({ gap }) => Css.pxToRem(-gap)};
	}

	list-style: none;
	padding: 0;
	margin: ${Css.pxToRem(16)} 0 0 0;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		--card-width: ${({ mobileCardWidth }) => mobileCardWidth}px;
		--gap: ${Css.pxToRem(16)};
		margin-top: 0;
		padding-top: 0;

		&:first-of-type {
			margin-left: -2px;
		}
	}

	display: flex;
	overflow-x: auto;
	overflow-y: hidden;
	gap: var(--gap);

	/* Add extra space before the first card to align with centered content */
	&::before {
		content: '';
		min-width: 16px;

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			min-width: 4px;
		}

		flex: 0 0 calc((100% - var(--content-width) - 24px) / 2);
	}

	/* Add extra space after the last card to allow scrolling to last card all the way to align left with the content */
	&::after {
		content: '';
		flex: 0 0
			calc(
				(100% - var(--card-width)) -
					(100% - var(--content-width) + var(--gap) * 2) / 2
			);

		@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
			flex: 0 0 calc((100% - var(--card-width)) - 20px * 2);
		}
	}

	&::-webkit-scrollbar {
		display: none;
	}

	/* Firefox */
	scrollbar-width: none;

	${StyledCarouselItem} {
		flex: 0 0 var(--card-width);
	}
`;

const NavigationSection = styled(Section)`
	position: relative;
	padding: ${Css.pxToRem(22)} 0 0 0;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding-top: ${Css.pxToRem(14)};
	}
`;
